/**
 * The realtime channel.
 *
 * Terrain arrives over HTTP because it stands still; this carries what changes
 * — who is here, who moved, and the one piece of news that invalidates
 * everything held in memory, the world being swapped. The wire format is
 * described in `crates/server/src/ws/protocol.rs`; this is the other half of it.
 *
 * What reaches this client is decided by where its **player stands**, not by
 * where its camera points: the server sends what is near the pawn and nothing
 * else, and somebody stepping out of that range arrives as a departure. So a
 * pawn removed here is not necessarily somebody who logged off — it is somebody
 * this player can no longer see, which for a client is the same thing.
 *
 * It reconnects on its own, with a widening gap, because a dropped socket on a
 * train is not a reason to throw somebody back to a login screen — the session
 * token is still good and the server still knows where they were standing. It
 * stops trying only when the server says the session is not valid, or when the
 * world it was connected to stopped being the live one: both are answers, not
 * accidents, and retrying them would loop.
 */

import { Placed } from '../game/buildings/Placed';
import { RoadCell } from './Roads';
import { ConvoyView } from './Haulage';
import type { ShipView } from './Ships';
import { Axial } from '../game/map/HexCoordinates';

/**
 * Where the socket is.
 *
 * Derived from the page rather than written down, for the same reason the API
 * address is relative: one build has to work behind whatever hostname it is
 * served from. The scheme follows the page's — a page on `https` may not open
 * a plain `ws`, and a build that hard-coded one would work in development and
 * be refused by the browser the moment it was served over TLS.
 */
const WS =
  import.meta.env.VITE_WS_URL ??
  `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;

/** First retry gap, doubled each time up to the ceiling. */
const RETRY_START_MS = 500;
const RETRY_MAX_MS = 15_000;

/** Silence after which the connection is presumed dead rather than idle. */
const PING_MS = 20_000;

export interface Presence {
  playerId: string;
  username: string;
  position: Axial;
  /**
   * Które z sześciu stanowisk pola zajmuje ten pionek.
   *
   * **Liczy je serwer**, bo tylko on wie, z której strony ktoś wszedł — a pole
   * mieści sześcioro i miejsce na nim jest częścią tego, gdzie ktoś stoi, nie
   * ozdobą. Klient, który dopiero zobaczył kogoś w zasięgu, nie ma jak tego
   * wyprowadzić z niczego, co widział.
   */
  slot: number;
  /** Wersja sztandaru, jeśli gracz go ma — obrazek pobiera się osobno. */
  banner?: number;
}

/** One line of a store: a material key and how much of it there is. */
export interface Item {
  material: string;
  amount: number;
}

/**
 * What one of your own buildings holds and makes.
 *
 * Only ever about **your** buildings: a store is the owner's business, so the
 * public view of a building carries none of this and it travels by a message
 * addressed to one player — the same arrangement the purse uses.
 */
/** Jeden surowiec, który budynek robi, i jak się to liczy. */
export interface Made {
  material: string;
  /**
   * Ile tego wypada na jedną sztukę zdobyczy. Jeden wszędzie, gdzie budynek
   * robi jedną rzecz — i przez tę liczbę przelicza się `perHour` oraz to, co
   * ta strona dolicza sobie sama, na sztuki surowca.
   */
  each: number;
  /** Pułap tego surowca: pojemność szczebla razy `each`. */
  capacity: number;
}

export interface Holding {
  id: string;
  store: Item[];
  /**
   * Co robi: wiersz na surowiec, z gruntem wokół wliczonym w stawkę.
   *
   * **Lista, bo jedna wyprawa przynosi kilka rzeczy naraz** — chata myśliwych
   * robi skórę i dwa mięsa na godzinę, a to jest jedna produkcja i dwa wiersze.
   * Pusta u budynku, który nie robi nic, i u tego, co jeszcze stoi
   * w rusztowaniu.
   */
  makes?: Made[];
  /** Sztuk zdobyczy na godzinę; ile z tego czego, mówi `makes`. */
  perHour?: number;
  /** Stawka z katalogu, zanim ziemia cokolwiek do niej dołożyła. */
  base?: number;
  /**
   * Dlaczego stawka jest taka, a nie bazowa — wiersz na powód.
   *
   * **Gotowe zdania, nie dane.** Złożenie ich wymaga słowników biomów,
   * obiektów i materiałów naraz, a wpis modyfikatora w katalogu jest bezimienny
   * („łąka" zapisana jest jako umiarkowany klimat bez lasu). Serwer ma te
   * słowniki i mówi jednym zdaniem.
   */
  modifiers?: { label: string; effect: string }[];
  /**
   * Ilu mieszka w tej osadzie.
   *
   * Brak pola u wszystkiego, co osadą nie jest — „zero mieszkańców" i „to nie
   * jest osada" to dwa różne zdania. Liczba, nie krzywa: rośnie o kilkadziesiąt
   * na godzinę, więc nie ma czego animować, a wzór zostaje po jednej stronie
   * drutu.
   */
  people?: number;
  /**
   * Seconds until one more appears, as of the moment this was sent.
   *
   * A phase, not a state: with `perHour` for a period and this for an offset,
   * the store counts itself up on this side — the same arrangement the build bar
   * and the day phase use, and the reason the server pushes nothing while a
   * building quietly fills. Absent when nothing more is coming, which is a full
   * store and a building that makes nothing.
   */
  nextIn?: number;
}

/** Na kogo się wskazuje: pionek albo wóz. */
export type Mark = { kind: 'player'; id: string } | { kind: 'convoy'; id: string };

/**
 * Druga strona rozmowy, tyle, ile trzeba na nagłówek panelu.
 *
 * Nazwa jest opcjonalna, bo wóz jej nie ma — jedzie bez podpisu i na mapie,
 * i tutaj.
 */
export interface Partner {
  kind: 'player' | 'convoy';
  id: string;
  name?: string;
}

/**
 * Jedna kwestia do wybrania: etykieta na przycisk, identyfikator na drut.
 *
 * `enabled` przychodzi z serwera i dziś znaczy jedno: zatrzymany nie odzywa się
 * pierwszy. Kwestia, której nie wolno wybrać, jedzie mimo to — wyszarzona mówi
 * „poczekaj", a wycięta mówiłaby „nie masz nic do powiedzenia".
 */
export interface Choice {
  id: string;
  label: string;
  enabled: boolean;
}

/**
 * Jedna kwestia, która już padła.
 *
 * `mine` liczy **serwer**, osobno dla każdej strony: ta sama kwestia jest moja
 * u tego, kto ją wybrał, i cudza u drugiego. Klient nie porównuje tu niczyich
 * identyfikatorów, bo nie musi.
 */
export interface Line {
  mine: boolean;
  label: string;
}

/**
 * Rozmowa taka, jaka jest w tej chwili — cała, a nie to, co się zmieniło.
 *
 * Panel odtwarza się z jednej wiadomości: nie ma po tej stronie listy kwestii
 * do dopisywania, którą dałoby się rozjechać z serwerem przez jedną zgubioną
 * ramkę. `partner` równy `null` znaczy „z nikim" — rozmowa się skończyła.
 */
export interface Talking {
  partner: Partner | null;
  /** Ile sekund zostało. Brak przy wozie; odnawiane każdą kwestią. */
  seconds: number | null;
  /** Czy to ja zagadałem. Tylko zagadujący wychodzi przed czasem. */
  mine: boolean;
  /** Co mogę teraz wybrać. Puste, gdy kwestie się wyczerpały. */
  options: Choice[];
  /** Co już padło, w kolejności. */
  log: Line[];
}

export interface Welcome {
  playerId: string;
  /** Czy trwa tryb testowy — nieobecne, gdy nie trwa. */
  godmode?: boolean;
  username: string;
  world: { id: string };
  position: Axial;
  /** Własne stanowisko na polu — patrz `Presence.slot`. */
  slot: number;
  players: Presence[];
  /** What this player is carrying, as of the greeting. */
  purse: Item[];
  /**
   * What this player's own buildings hold and make, as of the greeting.
   *
   * Optional for the same reason `daylight` is: a server older than this sends
   * none, and a tooltip with no store in it is a tooltip that says less rather
   * than a client that breaks.
   */
  estate?: Holding[];
  /**
   * Osada, w której ten gracz śpi, o ile śpi.
   *
   * W powitaniu, bo **odpoczynek przeżywa wylogowanie**: kto poszedł spać
   * i zamknął kartę, wraca do łóżka. Klient bez tej informacji narysowałby
   * własnego pionka stojącego na łące, na której nie stoi.
   */
  resting?: string | null;
  /**
   * The world's clock: where in the day it is, how long a day lasts and how
   * much of it is night.
   *
   * Optional, because a server older than the sky sends none — the client then
   * simply never starts its own clock and the light stays where it was.
   */
  daylight?: Sky;
}

/**
 * The world's sky: where in the day it is, and what the weather is doing.
 *
 * Arrives in the greeting and again whenever the panel moves it. It used to
 * arrive once and only once — the day was a pure function of the wall clock and
 * needed no telling twice — and the moment an administrator could change it,
 * that had to become a thing the server says out loud.
 */
export interface Sky {
  phase: number;
  daySeconds: number;
  nightShare: number;
    /**
     * The wall clock the phase was read from, in seconds.
     *
     * The phase wraps every three hours and the weather does not, so anything
     * slower than a day reads this instead. Optional for the same reason the
     * rest of this block is: a server older than it sends none.
     */
  clock?: number;
  /** Whether an administrator is holding the day still. */
  paused?: boolean;
  /** Cloud cover pinned from the panel. Absent when the weather runs itself. */
  cover?: number;
}

export type Status = 'connecting' | 'live' | 'retrying' | 'closed';

export interface Handlers {
  onWelcome(welcome: Welcome): void;
  onJoined(player: Presence): void;
  /**
   * `into` przychodzi, gdy ktoś znika pod dachem: pole osady, na które ma
   * jeszcze wejść, zanim zniknie.
   */
  onLeft(playerId: string, into?: Axial): void;
  onMoved(playerId: string, position: Axial, slot: number): void;
  /**
   * Czy ten gracz śpi, i gdzie.
   *
   * Adresowana do niego i do nikogo więcej — z zewnątrz odpoczynek jest
   * `playerLeft`, a wstanie `playerJoined`. Ta wiadomość istnieje, bo **budzi
   * serwer**: cokolwiek wymagającego ciała na mapie stawia człowieka na nogi,
   * więc klient nie może znać swojego stanu z tego, o co sam poprosił.
   */
  onRest(building: string | null, slot: number): void;
  /** The route the server is about to walk us along. */
  onRoute(cells: Axial[]): void;
  /**
   * Z kim rozmawiamy, ile zostało i po której stronie stoimy.
   *
   * **Jedno wywołanie na początek, każdą kwestię i koniec**, jak przy wozie:
   * pusty `partner` znaczy „z nikim", a panel rysuje to, co jest teraz.
   * `seconds` jest pusty przy wozie — ten nie ma terminu; `mine` mówi, czy
   * wolno wyjść przed czasem, bo zatrzymany nie wychodzi.
   */
  onTalking(talk: Talking): void;
  /** The sky was moved from the panel — a different hour, or pinned weather. */
  onSky(sky: Sky): void;
  /**
   * Tryb testowy się przestawił: wszystko za jedną sztukę i w pięć sekund.
   *
   * Dotyczy wszystkich naraz i przychodzi tą samą drogą, co niebo — bo ceny
   * i czasy klient rysuje sam, więc musi wiedzieć, że reguła się zmieniła.
   */
  onGodmode(on: boolean): void;
  /**
   * The same building, different: today that means a rung.
   *
   * Separate from `onBuildingPlaced` because they mean different things — one
   * is a new thing on the map, the other is the same thing changed, and a
   * client treating them alike would draw two huts on one cell.
   */
  onBuildingChanged(building: Placed): void;
  /**
   * Drogi, które właśnie powstały — cały odcinek jednym zdarzeniem.
   *
   * Delta i tylko delta, jak przy postawieniu budynku: co biegnie w obszarze,
   * przychodzi razem z terenem, a to jest to, co się od tamtej pory zmieniło.
   */
  onRoads(cells: RoadCell[]): void;
  /**
   * Drzewa zniknęły z tych pól. `by` i `logs` mówią, kto wyciął i ile wziął —
   * chmurkę z kłodami dostaje tylko on.
   */
  onFelled(cells: Axial[], by: string, logs: number): void;
  /** Ktoś zaczął rąbać pole — rąbie z `from`, skończy za `seconds`. */
  onFellingStarted(cell: Axial, by: string, from: Axial, seconds: number): void;
  /** Gracz dostał sztandar albo go stracił (moderacja), albo zmienił się jego ród. */
  onBanner(playerId: string, banner: number | undefined, house: string | undefined): void;
  /** Wycinka przerwana, drzewa zostają. */
  onFellingStopped(cell: Axial, by: string): void;
  /**
   * Wóz: pojawił się, przeszedł na inne pole albo zniknął.
   *
   * **Jedno wywołanie na trzy wiadomości**, bo to jest jedno zdanie o tym
   * samym: gdzie ten wóz teraz jest. `null` znaczy „nigdzie" — dojechał albo
   * odjechał poza zasięg widzenia, a jedno i drugie znaczy dla mapy to samo.
   */
  onConvoy(id: string, at: Axial | null): void;
  /**
   * Statek: stanął na wodzie albo go już nie ma.
   *
   * **Jedno wywołanie, jak przy wozie**, i z tego samego powodu: dla mapy
   * „powstał", „wpłynął w zasięg" i „przesunął się" to jedno zdanie — gdzie ten
   * statek teraz jest. `null` znaczy „nigdzie".
   */
  onShip(ship: ShipView | null, id?: string): void;
  /**
   * Statek przeszedł na sąsiednie pole.
   *
   * **Osobno od `onShip`, choć obie mówią „gdzie jest"**: tamta niesie cały
   * statek — świeżo położony albo taki, który wpłynął w zasięg — a ta jeden
   * krok, więc pionek ma się po tej stronie **przesunąć**, a nie przeskoczyć.
   */
  onShipMoved(id: string, at: Axial): void;
  /**
   * Ile serwerowi zajęła trasa, którą właśnie przebyliśmy — **pomiar, nie
   * mechanika**. Obraz sunie własnym zegarem, a to jedyna droga, którą da się
   * powiedzieć, o ile te dwa zegary się rozjeżdżają.
   */
  onTravelled(took: number, planned: number): void;
  /**
   * Wóz zabrał towar z twojego zakładu.
   *
   * **Osobno od `onConvoy`, choć obie wiadomości powstają w jednej chwili**, bo
   * mówią co innego: tamta niesie wóz do narysowania i słyszy ją każdy w
   * zasięgu, ta niesie ładunek i idzie wyłącznie do właściciela. Filtr „tylko
   * moje" jest więc po stronie serwera, a nie obietnicą tego pliku.
   */
  onShipped(building: string, material: string, amount: number): void;
  /**
   * Somebody put a building up near enough to see.
   *
   * Only the placement comes this way. What a building *is* comes from the
   * catalogue, and what already stands in an area comes with the terrain —
   * this is the delta and nothing else.
   */
  onBuildingPlaced(building: Placed): void;
  /**
   * What this player is carrying now.
   *
   * Sent when it changes and to nobody else. The greeting carries the first
   * one, so a client that never hears this still starts with the right numbers.
   */
  onPurse(items: Item[]): void;
  /**
   * What your own buildings hold and make, now.
   *
   * Same contract as `onPurse`: sent when it changes, to nobody else, and the
   * greeting already carried the first one.
   */
  onEstate(buildings: Holding[]): void;
  /** The panel made a different world live; everything held is stale. */
  onWorldChanged(worldId: string): void;
  onError(code: string, message: string): void;
  onStatus(status: Status): void;
}

export class Connection {
  private socket: WebSocket | null = null;
  private retry = RETRY_START_MS;
  private timer: number | null = null;
  private heartbeat: number | null = null;
  private stopped = false;

  constructor(
    private readonly token: string,
    private readonly handlers: Handlers
  ) {}

  open(): void {
    this.stopped = false;
    this.connect();
  }

  /** Deliberate close: no retry follows. */
  close(): void {
    this.stopped = true;
    this.clearTimers();
    this.socket?.close();
    this.socket = null;
    this.handlers.onStatus('closed');
  }

  /**
   * Idź albo płyń do pola — **razem z trasą, którą było widać**.
   *
   * Serwer sprawdza każdy jej krok i to jego odpowiedź się liczy; bez trasy
   * szuka sam, po staremu. Powód jest ten sam, co przy kładzeniu drogi: wiele
   * tras ma identyczny koszt, a dwa poprawne A\* wybierają z remisu różne — więc
   * gracz szedłby inaczej, niż widział pod kursorem. Na wodzie remisów są setki
   * i rozjazd było widać najmocniej.
   */
  goTo(cell: Axial, cells?: Axial[]): void {
    this.send({
      type: 'goTo',
      q: cell.q,
      r: cell.r,
      cells: cells?.map((step) => ({ q: step.q, r: step.r })),
    });
  }

  stopWalking(): void {
    this.send({ type: 'stop' });
  }

  /**
   * Idź za kimś. `talk` znaczy „i zagadaj, gdy dojdziesz".
   *
   * Celem jest **pionek albo wóz, nie pole**: trasę wyznacza serwer i wyznacza
   * ją od nowa, ilekroć tamten się ruszy. Klient nie wie, gdzie ten pościg
   * pójdzie, i to jest ta sama umowa, co przy chodzeniu.
   */
  follow(target: Mark, talk: boolean): void {
    this.send({ type: 'follow', target, talk });
  }

  /** Skończ rozmowę, którą prowadzimy. */
  part(): void {
    this.send({ type: 'part' });
  }

  /**
   * Powiedz jedną kwestię.
   *
   * Odsyłany jest **identyfikator, który przyszedł w `options`**, a nie zdanie:
   * treść należy do słownika na serwerze, a to, czy wolno ją teraz wybrać,
   * rozstrzyga on sam. Ta sama wiadomość z obu stron rozmowy.
   */
  say(action: string): void {
    this.send({ type: 'say', action });
  }

  /**
   * Where the camera came to rest.
   *
   * Sent when it settles rather than while it moves: this is a thing to come
   * back to, not a stream to follow, and a message per frame of a drag would be
   * a hundred a second for a number nobody reads until the next login.
   */
  look(yaw: number, zoom: number): void {
    this.send({ type: 'view', yaw, zoom });
  }

  private send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
  }

  private connect(): void {
    this.handlers.onStatus(this.retry === RETRY_START_MS ? 'connecting' : 'retrying');

    const socket = new WebSocket(`${WS}/ws`);
    this.socket = socket;

    socket.onopen = () => {
      // The token rides in the first frame rather than the URL: query strings
      // end up in proxy logs and browser history, frames do not.
      socket.send(JSON.stringify({ type: 'hello', token: this.token }));
      this.startHeartbeat();
    };

    socket.onmessage = (event) => this.receive(String(event.data));

    socket.onclose = () => {
      this.clearTimers();
      if (this.stopped) return;

      this.handlers.onStatus('retrying');
      this.timer = window.setTimeout(() => this.connect(), this.retry);
      this.retry = Math.min(this.retry * 2, RETRY_MAX_MS);
    };

    // `onerror` is always followed by `onclose`, which is where the retry
    // lives. Handling both would schedule two.
    socket.onerror = () => {};
  }

  private receive(raw: string): void {
    let message: { type: string } & Record<string, unknown>;
    try {
      message = JSON.parse(raw);
    } catch {
      console.warn('niezrozumiała ramka z serwera:', raw.slice(0, 120));
      return;
    }

    switch (message.type) {
      case 'welcome':
        // A successful hello is what proves the connection works, so the retry
        // gap resets here rather than on the socket opening.
        this.retry = RETRY_START_MS;
        this.handlers.onStatus('live');
        this.handlers.onWelcome(message as unknown as Welcome);
        break;

      case 'playerJoined':
        this.handlers.onJoined(message as unknown as Presence);
        break;

      case 'playerLeft':
        this.handlers.onLeft(message.playerId as string, message.into as Axial | undefined);
        break;

      case 'playerMoved':
        this.handlers.onMoved(
          message.playerId as string,
          message.position as Axial,
          (message.slot as number | undefined) ?? 0
        );
        break;

      case 'rest':
        this.handlers.onRest(
          (message.building as string | null) ?? null,
          (message.slot as number | undefined) ?? 0
        );
        break;

      case 'route':
        this.handlers.onRoute(message.cells as Axial[]);
        break;

      case 'talking':
        this.handlers.onTalking({
          partner: (message.partner as Partner | null) ?? null,
          seconds: (message.seconds as number | null) ?? null,
          mine: message.mine === true,
          options: (message.options as Choice[] | undefined) ?? [],
          log: (message.log as Line[] | undefined) ?? [],
        });
        break;

      case 'convoySet': {
        const convoy = message.convoy as ConvoyView;
        this.handlers.onConvoy(convoy.id, { q: convoy.q, r: convoy.r });
        break;
      }

      case 'convoyMoved':
        this.handlers.onConvoy(message.convoyId as string, message.position as Axial);
        break;

      case 'convoyGone':
        this.handlers.onConvoy(message.convoyId as string, null);
        break;

      case 'shipSet':
        this.handlers.onShip(message.ship as ShipView);
        break;

      case 'travelled':
        this.handlers.onTravelled(message.took as number, message.planned as number);
        break;

      case 'shipMoved':
        this.handlers.onShipMoved(message.shipId as string, message.position as Axial);
        break;

      case 'shipGone':
        this.handlers.onShip(null, message.shipId as string);
        break;

      case 'shipped':
        this.handlers.onShipped(
          message.building as string,
          message.material as string,
          message.amount as number
        );
        break;

      case 'roadsLaid':
        this.handlers.onRoads(message.cells as RoadCell[]);
        break;

      case 'bannerChanged':
        this.handlers.onBanner(
          message.playerId as string,
          (message.banner as number | null | undefined) ?? undefined,
          (message.house as string | null | undefined) ?? undefined
        );
        break;

      case 'fellingStarted':
        this.handlers.onFellingStarted(
          message.cell as Axial,
          message.by as string,
          message.from as Axial,
          message.seconds as number
        );
        break;

      case 'fellingStopped':
        this.handlers.onFellingStopped(message.cell as Axial, message.by as string);
        break;

      case 'treesFelled':
        this.handlers.onFelled(message.cells as Axial[], message.by as string, message.logs as number);
        break;

      case 'buildingChanged':
        this.handlers.onBuildingChanged(message.building as Placed);
        break;

      case 'sky':
        this.handlers.onSky(message.sky as Sky);
        break;

      case 'godmode':
        this.handlers.onGodmode(message.on === true);
        break;

      case 'estate':
        this.handlers.onEstate((message.buildings ?? []) as Holding[]);
        break;
      case 'purse':
        this.handlers.onPurse((message.items ?? []) as Item[]);
        break;

      case 'buildingPlaced':
        this.handlers.onBuildingPlaced(message.building as Placed);
        break;

      case 'worldChanged':
        // Not an error and not something to retry: the ground under this
        // session is gone, and the server closes the socket behind this.
        this.stopped = true;
        this.handlers.onWorldChanged(message.worldId as string);
        break;

      case 'pong':
        break;

      case 'error': {
        const code = message.code as string;
        // A refused session will be refused again in half a second and in
        // fifteen; only a new token helps, so the retry loop stops here.
        //
        // `idle` is the other one, and it is the whole point of the server
        // saying it out loud: it shows out somebody who has done nothing for
        // half an hour, and a client that reconnected on its own would put them
        // straight back in and buy the server nothing.
        if (code === 'auth' || code === 'timeout' || code === 'idle') {
          this.stopped = true;
        }
        this.handlers.onError(code, message.message as string);
        break;
      }

      default:
        console.warn('nieznany typ wiadomości:', message.type);
    }
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    // A socket that is idle for minutes gets closed by proxies without either
    // side noticing; a ping every twenty seconds keeps it accounted for.
    this.heartbeat = window.setInterval(() => this.send({ type: 'ping' }), PING_MS);
  }

  private clearHeartbeat(): void {
    if (this.heartbeat !== null) {
      window.clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
